import { safeDestroys } from '@Common/Core/Destroyable';
import ColorHelper from '@Common/Helpers/ColorHelper';
import pointerHelper from '@Common/Helpers/PointerHelper';
import { basicColors as commonBasicColors, IColor } from './';
import { InputDropDown } from './InputDropDown';
import { Button } from './SimpleButton';

export const colorPicker = function (param?) {
    let onOkClick = _.noop;
    let onCancelClick = _.noop;

    param = $.extend(
        {
            defaultValue: '',
            control: 'hue',
            animationSpeed: 100,
            animationEasing: 'swing',
            showButton: false,
            useOpacity: true,
            namedColors: null
        },
        param
    );

    const _commonBasicColors = param.basicColors || commonBasicColors;

    let changed = _.noop;
    const root = $('<div class="color-picker" />');

    root.append(
        '<div class="minicolors-panel minicolors-slider-' +
            param.control +
            '">' +
            '<div class="minicolors-grid-container">' +
            '<div class="minicolors-grid">' +
            '<div class="minicolors-grid-inner"></div>' +
            '<div class="minicolors-picker"><span></span></div>' +
            '</div>' +
            '</div>' +
            '<div class="minicolors-slider-container">' +
            '<div class="minicolors-slider">' +
            '<div class="minicolors-picker"></div>' +
            '</div>' +
            '</div>' +
            '</div>'
    );

    if (param.useOpacity) {
        root.find('.minicolors-panel').append(
            '<div class="minicolors-opacity-slider-container">' +
                '<div class="minicolors-opacity-slider">' +
                '<div class="minicolors-picker"></div>' +
                '</div>' +
                '</div>'
        );
    } else {
        root.addClass('hide-opacity');
    }

    const inputContainer = $('<div class=\'color-input-container\'/>');
    const basicColors = Object.keys(param.namedColors || {})
        .sort()
        .map(name => ({ name: name, rgba: ColorHelper.getColorFromString(param.namedColors[name]) }));
    basicColors.addRange(_.orderBy(_commonBasicColors, 'name'));

    const basicColorItems = _.map(basicColors, c => {
        const html = $('<div/>');

        $('<span class="color-li-content"/>')
            .css({
                backgroundColor: rgb2hex(c.rgba)
            })
            .appendTo(html);
        $('<span class="color-li-name1"/>').text(c.name).appendTo(html);

        return {
            name: c.name,
            rgba: c.rgba,
            html: html.html()
        };
    });

    const inputDropDown = new InputDropDown({
        width: null,
        height: 34,
        sortable: false,
        comparer: (a, b) => {
            if (a && a.name !== undefined && b && b.name !== undefined) {
                if (a.name == b.name) {
                    return 0;
                } else if (a.name > b.name) {
                    return 1;
                } else {
                    return -1;
                }
            }
        }
    });
    inputDropDown.getList().items(basicColorItems);
    inputDropDown.getControl().appendTo(inputContainer);
    inputDropDown.getList().onItemClick(data => {
        setColor(data.name);
        changed(data.rgba);
    });

    const colorInput = inputDropDown.getInput();
    colorInput.on('blur', () => {
        const currentInputVal = colorInput.val();
        const simpleColor = tryConvertColorToString(currentInputVal);
        const ahex = parseHex(currentInputVal);
        if (ahex || simpleColor) {
            updateFromInput(false);

            if (simpleColor) {
                changed(simpleColor.rgba);
            } else {
                changed($.extend({ a: ahex.opacity }, hex2rgb(ahex.hex)));
            }
        }
    });

    const swatch = $('<span class=\'minicolors-swatch\'/>');
    inputContainer.append(swatch);
    root.append(inputContainer);

    let onBtnClick = _.noop;
    let btnCancel, btnOk;
    if (param.showButton) {
        const buttonContainer = $('<div class=\'color-button-container\'/>');

        btnCancel = new Button({ text: strings.cancel });
        btnCancel.onClick(() => {
            onBtnClick(param.data);
            onCancelClick(param.data);
        });
        buttonContainer.append(btnCancel.getControl());

        btnOk = new Button({ text: strings.ok, css: { marginLeft: '10px' }, className: 'accept' });
        btnOk.onClick(() => {
            onBtnClick(param.data);
            onOkClick(param.data);
        });
        buttonContainer.append(btnOk.getControl());

        root.append(buttonContainer);
    }

    const rgbDefaultColor = getBasicColor(param.defaultValue) || ColorHelper.getColorFromAnyString(param.defaultValue);

    if (rgbDefaultColor) {
        setValueInternal(rgb2hex(rgbDefaultColor), param.useOpacity ? rgbDefaultColor.a : 1);
    } else {
        colorInput.attr('data-hex', '');
        colorInput.attr('data-opacity', 1);
    }

    // Moves the selected picker
    function move(target, event, animate?) {
        const picker = target.find('.minicolors-picker'),
            pickerHeight = picker.outerHeight(),
            pickerWidth = picker.outerWidth(),
            position = pointerHelper.getPosition(event, target);
        let x = position.x,
            y = position.y;
        const duration = animate ? param.animationSpeed : 0;
        let wx, wy, r, phi;

        // Constrain picker to its container
        if (x < 0) {
            x = 0;
        }
        if (y < 0) {
            y = 0;
        }
        if (x > target.width()) {
            x = target.width();
        }
        if (y > target.height()) {
            y = target.height();
        }

        // Constrain color wheel values to the wheel
        if (target.parent().is('.minicolors-slider-wheel') && picker.parent().is('.minicolors-grid')) {
            wx = 75 - x;
            wy = 75 - y;
            r = Math.sqrt(wx * wx + wy * wy);
            phi = Math.atan2(wy, wx);
            if (phi < 0) {
                phi += Math.PI * 2;
            }
            if (r > 75) {
                r = 75;
                x = 75 - 75 * Math.cos(phi);
                y = 75 - 75 * Math.sin(phi);
            }
            x = Math.round(x);
            y = Math.round(y);
        }

        // Move the picker
        if (target.is('.minicolors-grid')) {
            picker.stop(true).animate(
                {
                    top: y - pickerHeight / 2 + 'px',
                    left: x - pickerWidth / 2 + 'px'
                },
                duration,
                param.animationEasing,
                () => {
                    updateFromControl(target);
                    const rgba = rgbObject(colorInput);
                    rgba && changed(rgba);
                }
            );
        } else {
            picker.stop(true).animate(
                {
                    top: y - 1
                },
                duration,
                param.animationEasing,
                () => {
                    updateFromControl(target);
                    const rgba = rgbObject(colorInput);
                    rgba && changed(rgba);
                }
            );
        }
    }

    // Sets the input based on the color picker values
    function updateFromControl(target) {
        function getCoords(picker, container) {
            let left, top;
            if (!picker.length || !container) {
                return null;
            }
            left = picker.offset().left;
            top = picker.offset().top;
            const ycoord = top - container.offset().top; // + (picker.outerHeight() / 2);
            return {
                x: left - container.offset().left + picker.outerWidth() / 2,
                y: ycoord < 0 ? 0 : ycoord
            };
        }

        let hue,
            saturation,
            brightness,
            rgb,
            x,
            y,
            r,
            phi,
            hex = colorInput.attr('data-hex'),
            opacity = parseFloat(colorInput.attr('data-opacity'));
        const // Helpful references
            panel = root.find('.minicolors-panel'),
            swatch = root.find('.minicolors-swatch'),
            // Panel objects
            grid = root.find('.minicolors-grid'),
            slider = root.find('.minicolors-slider'),
            opacitySlider = root.find('.minicolors-opacity-slider'),
            // Picker objects
            gridPicker = grid.find('.minicolors-picker'),
            sliderPicker = slider.find('.minicolors-picker'),
            opacityPicker = opacitySlider.find('.minicolors-picker'),
            // Picker positions
            gridPos = getCoords(gridPicker, grid),
            sliderPos = getCoords(sliderPicker, slider);

        const opacityPos = getCoords(opacityPicker, opacitySlider);

        // Handle colors
        if (target.is('.minicolors-grid, .minicolors-slider')) {
            // Determine HSB values
            switch (param.control) {
                case 'wheel':
                    // Calculate hue, saturation, and brightness
                    x = grid.width() / 2 - gridPos.x;
                    y = grid.height() / 2 - gridPos.y;
                    r = Math.sqrt(x * x + y * y);
                    phi = Math.atan2(y, x);
                    if (phi < 0) {
                        phi += Math.PI * 2;
                    }
                    if (r > 75) {
                        r = 75;
                        gridPos.x = 69 - 75 * Math.cos(phi);
                        gridPos.y = 69 - 75 * Math.sin(phi);
                    }
                    saturation = keepWithin(r / 0.75, 0, 100);
                    hue = keepWithin((phi * 180) / Math.PI, 0, 360);
                    brightness = keepWithin(100 - Math.floor(sliderPos.y * (100 / slider.height())), 0, 100);
                    hex = hsb2hex({
                        h: hue,
                        s: saturation,
                        b: brightness
                    });

                    // Update UI
                    slider.css('backgroundColor', hsb2hex({ h: hue, s: saturation, b: 100 }));
                    break;

                case 'saturation':
                    // Calculate hue, saturation, and brightness
                    hue = keepWithin(gridPos.x * (360 / grid.width()), 0, 360);
                    saturation = keepWithin(100 - Math.floor(sliderPos.y * (100 / slider.height())), 0, 100);
                    brightness = keepWithin(100 - Math.floor(gridPos.y * (100 / grid.height())), 0, 100);
                    hex = hsb2hex({
                        h: hue,
                        s: saturation,
                        b: brightness
                    });

                    // Update UI
                    slider.css('backgroundColor', hsb2hex({ h: hue, s: 100, b: brightness }));
                    root.find('.minicolors-grid-inner').css('opacity', saturation / 100);
                    break;

                case 'brightness':
                    // Calculate hue, saturation, and brightness
                    hue = keepWithin(gridPos.x * (360 / grid.width()), 0, 360);
                    saturation = keepWithin(100 - Math.floor(gridPos.y * (100 / grid.height())), 0, 100);
                    brightness = keepWithin(100 - Math.floor(sliderPos.y * (100 / slider.height())), 0, 100);
                    hex = hsb2hex({
                        h: hue,
                        s: saturation,
                        b: brightness
                    });

                    // Update UI
                    slider.css('backgroundColor', hsb2hex({ h: hue, s: saturation, b: 100 }));
                    root.find('.minicolors-grid-inner').css('opacity', 1 - brightness / 100);
                    break;

                default:
                    // Calculate hue, saturation, and brightness
                    hue = keepWithin(360 - sliderPos.y * (360 / slider.height()), 0, 360);
                    saturation = keepWithin(Math.floor(gridPos.x * (100 / grid.width())), 0, 100);
                    brightness = keepWithin(100 - Math.floor(gridPos.y * (100 / grid.height())), 0, 100);
                    hex = hsb2hex({
                        h: hue,
                        s: saturation,
                        b: brightness
                    });

                    // Update UI
                    grid.css('backgroundColor', hsb2hex({ h: hue, s: 100, b: 100 }));
                    break;
            }
        }

        // Adjust case
        opacity = param.useOpacity
            ? Math.max(Math.round((1 - opacityPos.y / (opacitySlider.height() - opacityPicker.outerHeight())) * 100) / 100, 0)
            : 1;
        setValueInternal(hex, opacity);

        // Handle change event
        doChange(colorInput, hex, opacity, swatch);
    }

    function setValueInternal(hex, opacity, preserve?) {
        const strColor = opacity === 1.0 ? (tryConvertColorToString(hex2rgb(hex)) || { name: null }).name : null;
        const newStringValue = strColor || (param.useOpacity ? hex.replace('#', '#' + opacityToHex(opacity)) : hex);

        if (!preserve) {
            colorInput.val(newStringValue);
            if (newStringValue.indexOf('#') === -1) {
                inputDropDown.getList().selectedItems([{ name: newStringValue }]);
            }
        }

        colorInput.attr('data-hex', hex);
        colorInput.attr('data-opacity', opacity);
    }

    // Sets the color picker values from the input
    function updateFromInput(preserveInputValue, firstRun?) {
        let ahex, hex, hsb, opacity, x, y, r, phi;
        const // Helpful references
            swatch = root.find('.minicolors-swatch'),
            // Panel objects
            grid = root.find('.minicolors-grid'),
            gridHeight = 150,
            gridWidth = 150,
            slider = root.find('.minicolors-slider'),
            sliderWidth = 20,
            sliderHeight = 150,
            opacitySlider = root.find('.minicolors-opacity-slider'),
            opacitySliderHeight = 150,
            opacitySliderWidth = 20,
            // Picker objects
            gridPicker = grid.find('.minicolors-picker'),
            gridPickerWidth = /*gridPicker.width()*/ 12,
            gridPickerHeight = /*gridPicker.height()*/ 12,
            sliderPicker = slider.find('.minicolors-picker'),
            opacityPicker = opacitySlider.find('.minicolors-picker');

        // Determine hex/HSB values

        const simpleColor = tryConvertColorToString(colorInput.val());
        ahex = parseHex(simpleColor ? rgba2hex(simpleColor.rgba) : colorInput.val(), true);

        if (ahex === null) {
            return;
        }

        hex = convertCase(ahex.hex, param.letterCase);
        opacity = ahex.opacity;

        // Update input value

        setValueInternal(hex, opacity, preserveInputValue);

        // Set opacity picker position
        y = keepWithin(
            /*opacitySlider.height()*/ opacitySliderHeight - /*opacitySlider.height()*/ opacitySliderHeight * opacity,
            0,
            /*opacitySlider.height()*/ opacitySliderHeight
        );
        opacityPicker.css('top', y + 'px');

        // Update swatch
        swatch.css('opacity', opacity);
        swatch.css('backgroundColor', hex);

        hsb = hex2hsb(hex);

        // Determine picker locations
        switch (param.control) {
            case 'wheel':
                // Set grid position
                r = keepWithin(Math.ceil(hsb.s * 0.75), 0, grid.height() / 2);
                phi = (hsb.h * Math.PI) / 180;
                x = keepWithin(75 - Math.cos(phi) * r, 0, grid.width());
                y = keepWithin(75 - Math.sin(phi) * r, 0, grid.height());
                gridPicker.css({
                    top: y + 'px',
                    left: x + 'px'
                });

                // Set slider position
                y = 150 - hsb.b / (100 / grid.height());
                if (hex === '') {
                    y = 0;
                }
                sliderPicker.css('top', y + 'px');

                // Update panel color
                slider.css('backgroundColor', hsb2hex({ h: hsb.h, s: hsb.s, b: 100 }));
                break;

            case 'saturation':
                // Set grid position
                x = keepWithin((5 * hsb.h) / 12, 0, 150);
                y = keepWithin(grid.height() - Math.ceil(hsb.b / (100 / grid.height())), 0, grid.height());
                gridPicker.css({
                    top: y + 'px',
                    left: x + 'px'
                });

                // Set slider position
                y = keepWithin(slider.height() - hsb.s * (slider.height() / 100), 0, slider.height());
                sliderPicker.css('top', y + 'px');

                // Update UI
                slider.css('backgroundColor', hsb2hex({ h: hsb.h, s: 100, b: hsb.b }));
                root.find('.minicolors-grid-inner').css('opacity', hsb.s / 100);

                break;

            case 'brightness':
                // Set grid position
                x = keepWithin((5 * hsb.h) / 12, 0, 150);
                y = keepWithin(grid.height() - Math.ceil(hsb.s / (100 / grid.height())), 0, grid.height());
                gridPicker.css({
                    top: y + 'px',
                    left: x + 'px'
                });

                // Set slider position
                y = keepWithin(slider.height() - hsb.b * (slider.height() / 100), 0, slider.height());
                sliderPicker.css('top', y + 'px');

                // Update UI
                slider.css('backgroundColor', hsb2hex({ h: hsb.h, s: hsb.s, b: 100 }));
                root.find('.minicolors-grid-inner').css('opacity', 1 - hsb.b / 100);
                break;

            default:
                // Set grid position
                x = keepWithin(Math.ceil(hsb.s / (100 / /*grid.width()*/ gridWidth)), 0, /*grid.width()*/ gridWidth) - gridPickerHeight / 2;
                y =
                    keepWithin(
                        /*grid.height()*/ gridHeight - Math.ceil(hsb.b / (100 / /*grid.height()*/ gridHeight)),
                        0,
                        /*grid.height()*/ gridHeight
                    ) -
                    gridPickerWidth / 2;
                gridPicker.css({
                    top: y + 'px',
                    left: x + 'px'
                });

                // Set slider position
                y = keepWithin(
                    /*slider.height()*/ sliderHeight - hsb.h / (360 / /*slider.height()*/ sliderHeight),
                    0,
                    /*slider.height()*/ sliderHeight
                );
                sliderPicker.css('top', y + 'px');

                // Update panel color
                grid.css('backgroundColor', hsb2hex({ h: hsb.h, s: 100, b: 100 }));
                break;
        }

        // Handle change event
        if (!firstRun) {
            doChange(colorInput, hex, opacity, swatch);
        }
    }

    function opacityToHex(opacity) {
        return ('0' + Math.round(opacity * 255).toString(16)).slice(-2);
    }

    function hexToOpacity(hex) {
        return Math.round((parseInt(hex, 16) / 255) * 100) / 100;
    }

    // Runs the change and changeDelay callbacks
    function doChange(input, hex, opacity, div) {
        div.css({
            backgroundColor: hex,
            opacity: opacity
        });

        const fullColor = hex + (param.useOpacity ? opacity : '');

        // Only run if it actually changed
        if (fullColor !== input.data('minicolors-lastChange')) {
            // Remember last-changed value
            input.data('minicolors-lastChange', fullColor);

            // Fire change event
            if (param.change) {
                if (param.changeDelay) {
                    // Call after a delay
                    clearTimeout(input.data('minicolors-changeTimeout'));
                    input.data(
                        'minicolors-changeTimeout',
                        setTimeout(() => {
                            param.change.call(input.get(0), hex, opacity);
                        }, param.changeDelay)
                    );
                } else {
                    // Call immediately
                    param.change.call(input.get(0), hex, opacity);
                }
            }
            changed($.extend({ a: opacity }, hex2rgb(hex)));
        }
    }

    // Generates an RGB(A) object based on the input's value
    function rgbObject(input) {
        const rgb = hex2rgb($(input).attr('data-hex'));
        if (param.useOpacity) {
            const opacity = $(input).attr('data-opacity');
            return $.extend(rgb, { a: opacity !== undefined ? parseFloat(opacity) : 1 });
        } else {
            return rgb;
        }
    }

    // Genearates an RGB(A) string based on the input's value
    function rgbString(input, alpha) {
        const hex = parseHex($(input).val(), true).hex,
            rgb = hex2rgb(hex);
        let opacity = parseFloat($(input).attr('data-opacity'));
        if (!rgb) {
            return null;
        }
        if (isNaN(opacity)) {
            opacity = 1;
        }
        if (alpha) {
            return 'rgba(' + rgb.r + ', ' + rgb.g + ', ' + rgb.b + ', ' + opacity + ')';
        } else {
            return 'rgb(' + rgb.r + ', ' + rgb.g + ', ' + rgb.b + ')';
        }
    }

    // Converts to the letter case specified in settings
    function convertCase(string, letterCase) {
        return letterCase === 'uppercase' ? string.toUpperCase() : string.toLowerCase();
    }

    // Parses a string and returns a valid hex string when possible
    function parseHex(string, expand?) {
        string = string.replace(/[^A-F0-9]/gi, '');
        let opacity = 1;
        if (string.length !== 6 && string.length !== 8) {
            return null;
        }
        if (string.length === 8) {
            opacity = hexToOpacity(string.substr(0, 2));
            string = string.substr(2, 6);
        }
        return { hex: '#' + string, opacity: opacity };
    }

    // Keeps value within min and max
    function keepWithin(value, min, max) {
        if (value < min) {
            value = min;
        }
        if (value > max) {
            value = max;
        }
        return value;
    }

    // Converts an HSB object to an RGB object
    function hsb2rgb(hsb) {
        const rgb = {} as IColor;
        let h = Math.round(hsb.h);
        const s = Math.round((hsb.s * 255) / 100);
        const v = Math.round((hsb.b * 255) / 100);
        if (s === 0) {
            rgb.r = rgb.g = rgb.b = v;
        } else {
            const t1 = v;
            const t2 = ((255 - s) * v) / 255;
            const t3 = ((t1 - t2) * (h % 60)) / 60;
            if (h === 360) {
                h = 0;
            }
            if (h < 60) {
                rgb.r = t1;
                rgb.b = t2;
                rgb.g = t2 + t3;
            } else if (h < 120) {
                rgb.g = t1;
                rgb.b = t2;
                rgb.r = t1 - t3;
            } else if (h < 180) {
                rgb.g = t1;
                rgb.r = t2;
                rgb.b = t2 + t3;
            } else if (h < 240) {
                rgb.b = t1;
                rgb.r = t2;
                rgb.g = t1 - t3;
            } else if (h < 300) {
                rgb.b = t1;
                rgb.g = t2;
                rgb.r = t2 + t3;
            } else if (h < 360) {
                rgb.r = t1;
                rgb.g = t2;
                rgb.b = t1 - t3;
            } else {
                rgb.r = 0;
                rgb.g = 0;
                rgb.b = 0;
            }
        }
        return {
            r: Math.round(rgb.r),
            g: Math.round(rgb.g),
            b: Math.round(rgb.b)
        };
    }

    // Converts an RGB object to a hex string
    function rgb2hex(rgb) {
        const hex = [rgb.r.toString(16), rgb.g.toString(16), rgb.b.toString(16)];
        $.each(hex, (nr, val) => {
            if (val.length === 1) {
                hex[nr] = '0' + val;
            }
        });
        return '#' + hex.join('');
    }

    function rgba2hex(rgba) {
        const hex = [opacityToHex(rgba.a == null ? 1 : rgba.a), rgba.r.toString(16), rgba.g.toString(16), rgba.b.toString(16)];
        $.each(hex, (nr, val) => {
            if (val.length === 1) {
                hex[nr] = '0' + val;
            }
        });
        return '#' + hex.join('');
    }

    // Converts an HSB object to a hex string
    function hsb2hex(hsb) {
        return rgb2hex(hsb2rgb(hsb));
    }

    // Converts a hex string to an HSB object
    function hex2hsb(hex) {
        const hsb = rgb2hsb(hex2rgb(hex));
        if (hsb.s === 0) {
            hsb.h = 360;
        }
        return hsb;
    }

    // Converts an RGB object to an HSB object
    function rgb2hsb(rgb) {
        const hsb = { h: 0, s: 0, b: 0 };
        const min = Math.min(rgb.r, rgb.g, rgb.b);
        const max = Math.max(rgb.r, rgb.g, rgb.b);
        const delta = max - min;
        hsb.b = max;
        hsb.s = max !== 0 ? (255 * delta) / max : 0;
        if (hsb.s !== 0) {
            if (rgb.r === max) {
                hsb.h = (rgb.g - rgb.b) / delta;
            } else if (rgb.g === max) {
                hsb.h = 2 + (rgb.b - rgb.r) / delta;
            } else {
                hsb.h = 4 + (rgb.r - rgb.g) / delta;
            }
        } else {
            hsb.h = -1;
        }
        hsb.h *= 60;
        if (hsb.h < 0) {
            hsb.h += 360;
        }
        hsb.s *= 100 / 255;
        hsb.b *= 100 / 255;
        return hsb;
    }

    // Converts a hex string to an RGB object
    function hex2rgb(hex) {
        hex = parseInt(hex.indexOf('#') > -1 ? hex.substring(1) : hex, 16);
        return {
            r: hex >> 16,
            g: (hex & 0x00ff00) >> 8,
            b: hex & 0x0000ff
        };
    }

    $(document)
        // Stop moving
        .on('mouseup.minicolors touchend.minicolors', () => {
            root.removeData('minicolors-target');
        });
    // Handle events
    root
        // Start moving
        .on('mousedown.minicolors touchstart.minicolors', '.minicolors-grid, .minicolors-slider, .minicolors-opacity-slider', function (event) {
            const target = $(this);
            event.preventDefault();
            root.data('minicolors-target', target);
            move(target, event, true);
        })
        // Move pickers
        .on('mousemove.minicolors touchmove.minicolors', event => {
            const target = root.data('minicolors-target');
            if (target) {
                move(target, event);
            }
        })
        // Update on keyup
        .on('keyup.minicolors', 'input', event => {
            updateFromInput(true);
        });

    function tryConvertColorToString(rgbaObj): { rgba: IColor; name: string } {
        if (rgbaObj.r !== undefined && rgbaObj.g !== undefined && rgbaObj.b !== undefined) {
            return _(basicColors).find(
                c =>
                    !isNamedColor(c.name) &&
                    c.rgba.r === rgbaObj.r &&
                    c.rgba.g === rgbaObj.g &&
                    c.rgba.b === rgbaObj.b &&
                    c.rgba.a === (rgbaObj.a == null ? 1 : rgbaObj.a)
            );
        } else if (_.isString(rgbaObj)) {
            rgbaObj = ColorHelper.getColorName(rgbaObj).toLowerCase();
            return _(basicColors).find(c => c.name && c.name.toLowerCase() === rgbaObj);
        }

        return null;
    }

    function getBasicColor(rgbaObj) {
        const basic = tryConvertColorToString(rgbaObj);
        if (basic) {
            return basic.rgba;
        }
        return null;
    }

    function isNamedColor(color) {
        return typeof color === 'string' && param.namedColors && !!param.namedColors[color];
    }

    function setColor(color) {
        if (color) {
            const simpleColor = tryConvertColorToString(color);
            if (simpleColor) {
                inputDropDown.getList().selectedItems([{ name: simpleColor.name }]);
                colorInput.val(simpleColor.name);
                updateFromInput(true);
            } else {
                if (typeof color === 'string') {
                    color = ColorHelper.getColorFromString(color, true) || rgbDefaultColor;
                }
                if (color) {
                    const hexColor = rgba2hex(color);
                    colorInput.val(hexColor);
                }
                updateFromInput(false);
            }
        }
    }

    return {
        getControl: () => root,
        setState: (enabled: boolean) => {},
        getNamedColor: function () {
            const selecetedColor = colorInput.val() as string;
            if (isNamedColor(selecetedColor)) {
                return ColorHelper.getCustomColorName(selecetedColor);
            }
            return ColorHelper.getStringColor(this.getColor());
        },
        setNamedColor: color => {
            setColor(color);
        },

        setColor: setColor,
        getColor: () => {
            const rgbaObject = rgbObject(colorInput);
            return rgbaObject;
        },
        onOkClick: fn => {
            $.isFunction(fn) && (onOkClick = fn);
        },
        onCancelClick: fn => {
            $.isFunction(fn) && (onCancelClick = fn);
        },

        onButtonClick: fn => {
            if ($.isFunction(fn)) {
                onBtnClick = fn;
            }
        },
        onChange: fn => {
            if ($.isFunction(fn)) {
                changed = fn;
            }
        },
        adjust: () => {},
        destroy: () => {
            safeDestroys(root, btnOk, btnCancel, inputDropDown);
        }
    };
};

export { colorPicker as ColorPicker };
